import type { ParsedImport, SourceKind } from "../types";

const MAX_FILE_BYTES = 5 * 1024 * 1024;

type WorkerRequest =
  | { id: string; action: "list-sheets"; buffer: ArrayBuffer }
  | {
      id: string;
      action: "parse";
      sourceKind: Exclude<SourceKind, "json" | "paste">;
      fileName: string;
      text?: string;
      buffer?: ArrayBuffer;
      sheet?: string;
    };

type WorkerResponse =
  | { id: string; ok: true; sheets: string[] }
  | { id: string; ok: true; parsed: ParsedImport }
  | { id: string; ok: false; error: string };

function runWorker(request: WorkerRequest): Promise<WorkerResponse> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL("../workers/import.worker.ts", import.meta.url), {
      type: "module",
    });
    const timeout = window.setTimeout(() => {
      worker.terminate();
      reject(new Error("Import timed out. Simplify the file or export it as CSV."));
    }, 10_000);

    worker.addEventListener("message", (event: MessageEvent<WorkerResponse>) => {
      if (event.data.id !== request.id) return;
      window.clearTimeout(timeout);
      worker.terminate();
      resolve(event.data);
    });
    worker.addEventListener("error", () => {
      window.clearTimeout(timeout);
      worker.terminate();
      reject(new Error("The import worker could not read this file."));
    });
    worker.postMessage(request);
  });
}

function detectSourceKind(file: File): Exclude<SourceKind, "json" | "paste"> | "json" {
  const extension = file.name.split(".").pop()?.toLocaleLowerCase();
  if (extension === "txt") return "txt";
  if (extension === "csv") return "csv";
  if (extension === "tsv") return "tsv";
  if (extension === "xlsx") return "xlsx";
  if (extension === "json") return "json";
  throw new Error("Use TXT, CSV, TSV, XLSX, or a Pickscape JSON backup.");
}

export function validateFileSize(file: File): void {
  if (file.size > MAX_FILE_BYTES) {
    throw new Error("Files must be 5 MB or smaller.");
  }
  if (file.size === 0) {
    throw new Error("The selected file is empty.");
  }
}

export function getSourceKind(file: File): ReturnType<typeof detectSourceKind> {
  validateFileSize(file);
  return detectSourceKind(file);
}

export async function listWorkbookSheets(file: File): Promise<string[]> {
  validateFileSize(file);
  const response = await runWorker({
    id: crypto.randomUUID(),
    action: "list-sheets",
    buffer: await file.arrayBuffer(),
  });
  if (!response.ok) throw new Error(response.error);
  if (!("sheets" in response)) throw new Error("Workbook sheets could not be read.");
  return response.sheets;
}

export async function parseListFile(file: File, sheet?: string): Promise<ParsedImport> {
  validateFileSize(file);
  const sourceKind = detectSourceKind(file);
  if (sourceKind === "json") {
    throw new Error("Pickscape backups use the project restore flow.");
  }

  const request: WorkerRequest =
    sourceKind === "xlsx"
      ? {
          id: crypto.randomUUID(),
          action: "parse",
          sourceKind,
          fileName: file.name,
          buffer: await file.arrayBuffer(),
          sheet,
        }
      : {
          id: crypto.randomUUID(),
          action: "parse",
          sourceKind,
          fileName: file.name,
          text: await file.text(),
        };

  const response = await runWorker(request);
  if (!response.ok) throw new Error(response.error);
  if (!("parsed" in response)) throw new Error("The file produced no list data.");
  return response.parsed;
}

export function parsePaste(text: string): ParsedImport {
  const normalized = text.replace(/\r\n/gu, "\n").trim();
  if (!normalized) {
    return { sourceKind: "paste", rows: [], parseWarnings: [] };
  }
  const rows = normalized.includes("\t")
    ? normalized.split("\n").map((line) => line.split("\t"))
    : normalized.split("\n").map((line) => [line]);

  return {
    sourceKind: "paste",
    rows: rows.slice(0, 10_001),
    parseWarnings:
      rows.length > 10_001 ? ["Only 10,000 pasted records can be imported at once."] : [],
  };
}
